/**
 * Contratto fra server e client.
 *
 * Gli schemi zod sono l'unica definizione di cosa è un input valido: il server
 * li usa per validare le richieste, il client per validare i form prima di
 * inviarli. Un tipo che cambia qui cambia in entrambi i posti, o non compila.
 */
import { z } from 'zod';

export const ROLES = ['admin', 'operator'] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Amministratore',
  operator: 'Operatore',
};

export const MIN_PASSWORD_LENGTH = 10;
export const MAX_IMPORT_ROWS = 20_000;

/** Trasforma le stringhe vuote (o di soli spazi) in `null`. */
function blankToNull<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    schema,
  );
}

const optionalText = (max: number) =>
  blankToNull(z.string().trim().max(max, `Massimo ${max} caratteri`).nullable()).optional();

const requiredText = (max: number, message: string) =>
  z.string().trim().min(1, message).max(max, `Massimo ${max} caratteri`);

/** Data in formato `AAAA-MM-GG`, come la produce `<input type="date">`. */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data non valida')
  .refine((value) => !Number.isNaN(new Date(`${value}T00:00:00Z`).getTime()), 'Data non valida');

// ── Autenticazione ───────────────────────────────────────────────────────────

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email('Email non valida').max(200),
  password: z.string().min(1, 'Inserisci la password').max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Inserisci la password attuale').max(200),
  newPassword: z
    .string()
    .min(MIN_PASSWORD_LENGTH, `Almeno ${MIN_PASSWORD_LENGTH} caratteri`)
    .max(200),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ── Eventi ───────────────────────────────────────────────────────────────────

export const createEventSchema = z.object({
  name: requiredText(120, 'Il nome dell’evento è obbligatorio'),
  date: blankToNull(isoDateSchema.nullable()).optional(),
  notes: optionalText(2000),
});
export type CreateEventInput = z.infer<typeof createEventSchema>;

export const updateEventSchema = z.object({
  name: requiredText(120, 'Il nome dell’evento è obbligatorio').optional(),
  date: blankToNull(isoDateSchema.nullable()).optional(),
  notes: optionalText(2000),
});
export type UpdateEventInput = z.infer<typeof updateEventSchema>;

// ── Chip ─────────────────────────────────────────────────────────────────────

/**
 * Riga di import. Il codice arriva già normalizzato dal client, ma il server lo
 * rinormalizza comunque: la normalizzazione è un'invariante del database, non
 * una cortesia del chiamante.
 */
export const importChipRowSchema = z.object({
  code: z.string().min(1).max(120),
  team: optionalText(160),
  firstName: optionalText(120),
  lastName: optionalText(120),
  category: optionalText(120),
  uciId: optionalText(80),
  raceNumber: optionalText(40),
  race: optionalText(160),
});
export type ImportChipRowInput = z.infer<typeof importChipRowSchema>;

/**
 * Il carico di un'importazione.
 *
 * `chips` può essere vuoto **se** `updates` non lo è: capita che un file non
 * porti nessun codice nuovo e serva solo ad arricchire quelli già in elenco, e
 * in quel caso l'operatore sceglie «aggiorna» su tutte le righe. Vietarlo
 * significherebbe rifiutare l'unica cosa che stava chiedendo di fare. Quello che
 * non può succedere è un carico che non dice niente: nessun inserimento e nessun
 * aggiornamento.
 */
export const importChipsSchema = z
  .object({
    chips: z
      .array(importChipRowSchema)
      .max(MAX_IMPORT_ROWS, `Massimo ${MAX_IMPORT_ROWS} chip per importazione`),
    /**
     * Righe già presenti in elenco che l'operatore ha scelto di aggiornare con i
     * dati del file. Stessa forma delle righe da inserire: il server riempie solo
     * i campi che arrivano valorizzati e **non tocca mai** quelli assenti, così
     * un aggiornamento non può cancellare un dato già registrato.
     */
    updates: z
      .array(importChipRowSchema)
      .max(MAX_IMPORT_ROWS, `Massimo ${MAX_IMPORT_ROWS} chip per importazione`)
      .optional(),
  })
  .refine((data) => data.chips.length > 0 || (data.updates?.length ?? 0) > 0, {
    message: 'Nessun chip da importare',
    path: ['chips'],
  });
export type ImportChipsInput = z.infer<typeof importChipsSchema>;

/** Creazione manuale: usata quando un codice non esiste nell'elenco. */
export const createChipSchema = z.object({
  code: z.string().trim().min(1, 'Il codice è obbligatorio').max(120),
  team: optionalText(160),
  firstName: optionalText(120),
  lastName: optionalText(120),
  category: optionalText(120),
  raceNumber: optionalText(40),
  race: optionalText(160),
  /**
   * Crea il chip e lo segna subito come restituito, in un'unica operazione.
   * È il caso "codice sconosciuto al banco": l'operatore ha il transponder in
   * mano, e fargli fare due passaggi (crea, poi cerca, poi restituisci) davanti
   * a una fila non ha senso.
   */
  returnNow: z.boolean().default(false),
});
export type CreateChipInput = z.infer<typeof createChipSchema>;

// ── Utenti ───────────────────────────────────────────────────────────────────

export const createUserSchema = z.object({
  email: z.string().trim().toLowerCase().email('Email non valida').max(200),
  name: requiredText(120, 'Il nome è obbligatorio'),
  password: z
    .string()
    .min(MIN_PASSWORD_LENGTH, `Almeno ${MIN_PASSWORD_LENGTH} caratteri`)
    .max(200),
  role: roleSchema.default('operator'),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  name: requiredText(120, 'Il nome è obbligatorio').optional(),
  role: roleSchema.optional(),
  active: z.boolean().optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const resetPasswordSchema = z.object({
  password: z
    .string()
    .min(MIN_PASSWORD_LENGTH, `Almeno ${MIN_PASSWORD_LENGTH} caratteri`)
    .max(200),
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// ── Parametri di percorso ────────────────────────────────────────────────────

export const eventParamsSchema = z.object({
  eventId: z.string().uuid('Identificativo evento non valido'),
});
export type EventParams = z.infer<typeof eventParamsSchema>;

export const chipParamsSchema = eventParamsSchema.extend({
  chipId: z.string().uuid('Identificativo chip non valido'),
});
export type ChipParams = z.infer<typeof chipParamsSchema>;

export const userParamsSchema = z.object({
  userId: z.string().uuid('Identificativo utente non valido'),
});
export type UserParams = z.infer<typeof userParamsSchema>;
